// Patients registry (Blueprint §2 screen 4; parity F-PAT-*). Ports the SEMANTICS
// of legacy loadPatients / renderPatients / toggleSort / sortList / updateAssignBar
// / execBulkAssign / exportPatientsCSV (v20 lines ~1580-1607, 1738-1806,
// 3556-3585, 4741-4774) onto the 2.0 data layer — reading the SHARED CASES slice
// (data/cases.js, the single writer both the Board and this registry consume) so
// there is never a second capped query of the patient set.
//
// On screen: a searchable, sortable table over the FULL loaded set (search filters
// the whole cache, not a capped subset), scoped to the nurse's queue scope
// (data/scope.js). Admin-only CSV export. THREE DISTINCT empty states — genuinely
// empty · empty-because-scoped/searched (with the way out) · load-failed (retry) —
// never conflated. A client render WINDOW (legacy ptShown) shows the first N rows
// with a "Show more" that widens the window (no fetch).
//
// Bulk assign (canAssign): a checkbox column + an "N selected · Assign to… · Assign
// · Clear" bar over the list. The selection is held in MODULE STATE (a Set), NOT
// read from the DOM, so it SURVIVES a realtime re-render (the Phase-2 lesson: a
// colleague touching any case must not wipe 15 ticked boxes). Non-assigners get
// per-row claim-safe "Pick up" + "Assign ▾" on unassigned rows (the open model) —
// this is the LEGACY-POOL DRAIN surface the Today banner deep-links into.
//
// Outcome capture at closure (R-024) is reached from the patient workspace's More
// actions (the shared archive-modal.js), matching legacy.

import { store, SLICES } from '../state/store.js';
import { esc, csvSafe } from '../ui/esc.js';
import { toast } from '../ui/toast.js';
import { register } from '../ui/actions.js';
import { netErr } from '../errors.js';
import { STAGES, resolveStage, isClosedCase } from '../db/contract.js';
import { isAdmin, canAssign } from '../auth.js';
import { registerView, navigate } from './registry.js';
import { initials } from './shell.js';
import { progDotHTML, programmeBannerCopy, pillLabel } from '../programmes.js';
import { getScope, scopeMatch, scopeLabel, setScopeMode } from '../data/scope.js';
import { loadUsers, userName, shortName, usersMap, assignableUsers } from '../data/users.js';
import { bulkAssign } from '../data/assign.js';
import { openAssignModal, pickUp } from './assign-modal.js';
import { openWorkspace } from './workspace.js';
import { loadAllCases, forceReloadAllCases, allCases, casesLoaded } from '../data/cases.js';

// Client render window size (legacy PT_PAGE): show this many rows, "Show more"
// widens the window — a client slice, never a fetch.
export const PT_PAGE = 25;

// ─── SVG icons (no functional emoji per §3) ────────────────────────────────
/** @param {string} paths @param {number} [size] @returns {string} */
function ic(paths, size = 14) {
  return (
    `<svg class="ic" viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="currentColor" ` +
    'stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    paths +
    '</svg>'
  );
}
const IC_USER = '<path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>';
const IC_SEARCH = '<circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>';
const IC_DOWN = '<path d="M12 3v13"/><path d="M7 12l5 5 5-5"/><path d="M5 21h14"/>';
const IC_WARN = '<path d="M12 4L2.5 20h19z"/><path d="M12 10v4m0 3v.5"/>';

/** Deterministic avatar colour (port of legacy ac()). */
function avatarColor(name) {
  const s = String(name || '');
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 360;
  return `hsl(${h} 42% 42%)`;
}

// ─── Pure logic (unit-tested without a DOM) ────────────────────────────────

/**
 * Free-text search predicate over a case (legacy Cmd-K fields, run over the FULL
 * loaded set — never a capped subset). Matches name / reference / phone / facility
 * / county, case-insensitive, trimmed. A blank query matches everything. Pure.
 * @param {any} c case row
 * @param {string} query
 * @returns {boolean}
 */
export function matchesSearch(c, query) {
  const q = String(query || '').trim().toLowerCase();
  if (!q) return true;
  const hay = [
    c.patient_name,
    c.reference_number,
    c.patient_phone,
    c.treating_facility,
    c.facility_name,
    c.county,
  ]
    .map((x) => String(x === null || x === undefined ? '' : x).toLowerCase())
    .join(' ');
  return hay.includes(q);
}

/**
 * toggleSort (legacy): same key flips direction, a new key starts ascending.
 * Pure — returns the next sort state.
 * @param {{key: string|null, dir: 'asc'|'desc'}} state
 * @param {string} key
 * @returns {{key: string, dir: 'asc'|'desc'}}
 */
export function nextSort(state, key) {
  if (state && state.key === key) return { key, dir: state.dir === 'asc' ? 'desc' : 'asc' };
  return { key, dir: 'asc' };
}

/**
 * Resolve a case's OWNER display name, JOIN-FIRST.
 *
 * The loader already pays for `owner:app_users(full_name)` on every row of a
 * chunked >1000-row fetch — reading it means the honest name survives an empty
 * or RLS-narrowed staff directory, where userName() would degrade every owned
 * row to the indistinguishable "A team member" (and legacy exportPatientsCSV
 * used `c.owner?.full_name` precisely for this). The directory stays as the
 * fallback for rows whose join didn't come back. Pure — resolver injected.
 * @param {any} c case row
 * @param {(id: any) => string} [nameOf] directory resolver (defaults to the store-backed userName)
 * @returns {string}
 */
export function ownerNameOf(c, nameOf) {
  if (!c || !c.assigned_to) return 'Unassigned';
  if (c.owner && c.owner.full_name) return c.owner.full_name;
  return (nameOf || userName)(c.assigned_to);
}

/**
 * Stable sort a case list by the sort state (legacy sortList): owner sorts on the
 * resolved display name; date-ish keys by time; numeric-ish numerically; else
 * locale string. A null key leaves the order untouched. Pure — the owner-name
 * resolver is passed in so it tests without a store.
 * @param {any[]} cases
 * @param {{key: string|null, dir: 'asc'|'desc'}} state
 * @param {(id: any) => string} nameOf owner id → display name
 * @returns {any[]} a NEW sorted array
 */
export function sortCases(cases, state, nameOf) {
  const arr = (cases || []).slice();
  const key = state && state.key;
  if (!key) return arr;
  const d = state.dir === 'asc' ? 1 : -1;
  const resolve = nameOf || ((x) => String(x || ''));
  return arr.sort((a, b) => {
    if (key === 'owner') {
      // Join-first, same as the rendered cell — otherwise the Owner sort
      // compares identical "A team member" strings and silently does nothing.
      const an = a.assigned_to ? ownerNameOf(a, resolve) : '';
      const bn = b.assigned_to ? ownerNameOf(b, resolve) : '';
      return an.toLowerCase().localeCompare(bn.toLowerCase()) * d;
    }
    let va = a[key];
    let vb = b[key];
    if (va === null || va === undefined) va = '';
    if (vb === null || vb === undefined) vb = '';
    if (key.includes('date') || key.includes('_at') || key.includes('enrolled')) {
      return (new Date(va || 0).getTime() - new Date(vb || 0).getTime()) * d;
    }
    if (typeof va === 'number' || (va !== '' && !isNaN(parseFloat(va)))) {
      return (parseFloat(va || '0') - parseFloat(vb || '0')) * d;
    }
    return String(va).localeCompare(String(vb)) * d;
  });
}

/**
 * The registry meta line (legacy pts-meta): total cases, how many are in scope,
 * how many match the search, the render-window count when clipped, and the
 * programme suffix. Pure.
 *
 * SCOPE AND SEARCH ARE REPORTED SEPARATELY. Folding a search narrowing into the
 * "in scope" figure told a nurse who couldn't find a patient that the QUEUE
 * SCOPE was hiding them — so they'd widen a scope that was already showing
 * everyone, and read the remainder as excluded rather than merely unmatched.
 * @param {{total: number, inScope: number, matched?: number, hasQuery?: boolean, shown: number, scopeText: string, programmeName: string|null}} ctx
 * @returns {string}
 */
export function registryMeta(ctx) {
  const matched = ctx.matched === undefined ? ctx.inScope : ctx.matched;
  const base = `${ctx.total} case${ctx.total === 1 ? '' : 's'}`;
  const scope = ctx.inScope !== ctx.total ? ` · ${ctx.inScope} in scope (${ctx.scopeText})` : '';
  const search = ctx.hasQuery ? ` · ${matched} matching` : '';
  const win = ctx.shown < matched ? ` · showing ${ctx.shown} of ${matched}` : '';
  const prog = ctx.programmeName ? ` · ${ctx.programmeName}` : '';
  return base + scope + search + win + prog;
}

// ─── CSV export (admin-only; legacy exportPatientsCSV) ──────────────────────

// The legacy column set, in order. Kept verbatim so a v_20 workflow importing the
// export sees the same headers.
export const CSV_HEADERS = Object.freeze([
  'Reference', 'Patient name', 'Phone', 'County', 'Facility', 'Programme', 'Stage', 'Owner', 'Enrolled', 'Risk',
]);

/**
 * The raw (pre-escape) CSV cell values for one case (legacy row builder). Pure —
 * the programme + owner name resolvers are passed in.
 * @param {any} c case row
 * @param {{programmeName: (c: any) => string, ownerName: (id: any) => string}} res
 * @returns {string[]}
 */
export function csvCells(c, res) {
  return [
    c.reference_number || '',
    c.patient_name || '',
    c.patient_phone || '',
    c.county || '',
    c.treating_facility || c.facility_name || '',
    res.programmeName(c),
    c.current_stage || c.status || '',
    // Join-first (legacy `c.owner?.full_name || 'Unassigned'`) — a CSV that
    // says "A team member" in every Owner cell is worse than useless.
    ownerNameOf(c, res.ownerName),
    c.created_at ? new Date(c.created_at).toISOString().slice(0, 10) : '',
    c.risk_level || c.risk_bucket || 'standard',
  ];
}

/**
 * Quote + escape one CSV line: csvSafe() EVERY cell (formula-injection defence —
 * patient names come from the PUBLIC consent form), then wrap in quotes and double
 * any embedded quote. Pure. @param {ReadonlyArray<any>} cells @returns {string}
 */
export function csvLine(cells) {
  return cells.map((v) => '"' + csvSafe(v).replace(/"/g, '""') + '"').join(',');
}

/**
 * Build the full CSV text (headers + one line per case). BOM is added by the
 * caller at blob time. Pure — resolvers injected.
 * @param {any[]} cases
 * @param {{programmeName: (c: any) => string, ownerName: (id: any) => string}} res
 * @returns {string}
 */
export function buildPatientsCsv(cases, res) {
  const lines = (cases || []).map((c) => csvLine(csvCells(c, res)));
  return [csvLine(CSV_HEADERS), ...lines].join('\r\n');
}

/**
 * What a CSV export is allowed to contain. PURE, and the whole point of it is
 * the thing it refuses to do: it NEVER falls back to the full registry.
 *
 * The previous export read `_visibleRows.length ? _visibleRows : allCases()` —
 * so an admin on "My patients" with no matches, or a search with zero hits,
 * pressed Export and received EVERY patient in the programme (name, phone,
 * county, facility) under a toast that said "Exported N patients as CSV". An
 * empty view exports nothing, a failed load exports nothing, and a view that
 * has not finished loading exports nothing — each with its own reason so the
 * toast can say why.
 * @param {{visibleRows?: any[]|null, loaded?: boolean, loadError?: any}} ctx
 * @returns {{rows: any[], reason: 'ok'|'empty'|'not-loaded'|'load-error'}}
 */
export function exportPlan(ctx) {
  const c = ctx || {};
  if (c.loadError) return { rows: [], reason: 'load-error' };
  if (!c.loaded) return { rows: [], reason: 'not-loaded' };
  const rows = Array.isArray(c.visibleRows) ? c.visibleRows.slice() : [];
  return rows.length ? { rows, reason: 'ok' } : { rows: [], reason: 'empty' };
}

/**
 * PURE: what `_visibleIds` becomes after a registry render (review item 12,
 * second-opinion pass). `loadAllCases` keeps the previous case set on a
 * failed reload — the rows a bulk-assign selection was ticked against are
 * still the ones sitting underneath "Couldn't load patients" — so a load
 * error must leave the visible-id set alone, not empty it.
 *
 * Emptying it bought nothing for the export fix it sat beside: exportPlan
 * (above) already refuses on `loadError` directly, never by consulting
 * `_visibleIds`. What it did cost is the selection — `renderAssignBar` prunes
 * `_sel` against `_visibleIds`, so an admin who ticked 20 patients had every
 * tick silently deleted by a reload that merely failed, with no message,
 * contradicting this module's own rule that the selection is "only narrowed
 * when the user themselves narrows the view" (see renderAssignBar).
 * @param {boolean} loadError
 * @param {Set<string>} previous the id set from the last successful render
 * @param {Set<string>} matched the freshly scoped+searched id set
 * @returns {Set<string>}
 */
export function nextVisibleIds(loadError, previous, matched) {
  return loadError ? previous : matched;
}

// ─── Module state ──────────────────────────────────────────────────────────

let _wired = false;
let _query = '';
let _shown = PT_PAGE;
let _sort = /** @type {{key: string|null, dir: 'asc'|'desc'}} */ ({ key: null, dir: 'asc' });
// Bulk-assign selection — the SOURCE OF TRUTH (a Set of case_ids), NOT the DOM.
// Renders read it, so a realtime re-render can't lose the selection.
const _sel = new Set();
let _assignPick = ''; // the picked owner in the bulk bar (survives re-render)
// The case_ids currently PASSING scope + search — what the user can actually
// see. Bulk assign and CSV export are both bounded by this, never by the full
// loaded set (see renderAssignBar / onExport).
let _visibleIds = /** @type {Set<string>} */ (new Set());
// The scoped+searched rows in render order — the CSV export's source, so the
// file matches the screen.
let _visibleRows = /** @type {any[]} */ ([]);

// Owner focus (Phase 6, R-078). Set by the Admin console before it navigates
// here — "Jane owns 14 patients — reassign them first" needs somewhere to send
// the admin. It DELIBERATELY BYPASSES the queue scope: the admin has asked for
// one specific owner's caseload, and silently intersecting that with their own
// 'mine' scope would show zero rows and read as "she owns nobody" — the exact
// false all-clear this feature exists to prevent. A dismissible banner says so.
// It is ONE-SHOT. The focus survives re-renders of the registry but NOT a fresh
// entry into the view from anywhere else: an admin who follows the hand-off
// link, reads the caseload, goes to Today and comes back must not still be
// looking at a scope-bypassed single-owner registry while their scope pill says
// something different. openRegistryForOwner arms _pendingOwnerFocus; the view's
// render() consumes it, and any other route into the view consumes `null`.
/** @type {{uid: string, name: string}|null} */
let _ownerFocus = null;
/** @type {{uid: string, name: string}|null} */
let _pendingOwnerFocus = null;

/** Test-only: reset module state. @returns {void} */
export function _resetPatients() {
  _wired = false;
  _ownerFocus = null;
  _pendingOwnerFocus = null;
  _query = '';
  _shown = PT_PAGE;
  _sort = { key: null, dir: 'asc' };
  _sel.clear();
  _assignPick = '';
  _visibleIds = new Set();
  _visibleRows = [];
}

function doc() {
  return /** @type {any} */ (globalThis).document;
}
function mounted() {
  const d = doc();
  const ui = store.get(SLICES.UI) || {};
  return !!d && ui.view === 'patients' && !!d.getElementById('patients-view');
}
function session() {
  return store.get(SLICES.SESSION) || {};
}
function selfId() {
  const s = session();
  return s.user && s.user.id;
}
function role() {
  return session().role;
}
function scope() {
  return getScope(selfId(), role());
}
function simpleMode() {
  return (store.get(SLICES.UI) || {}).mode === 'simple';
}
/** Bulk assign is available to assignment-capable staff, and never in simple mode. */
function canBulk() {
  return canAssign(role()) && !simpleMode();
}
function programmeHex(code) {
  const prog = store.get(SLICES.PROGRAMMES) || {};
  const p = prog.byCode && prog.byCode[code];
  return p && p.brand_accent_hex;
}
function programmeName(c) {
  const prog = store.get(SLICES.PROGRAMMES) || {};
  const code = c.programme_code;
  return code ? pillLabel(code, prog.byCode || {}) : 'Unknown';
}

// ─── Render (store → DOM) ──────────────────────────────────────────────────

function renderRegistry() {
  if (!mounted()) return;
  const d = doc();
  const list = d.getElementById('pts-list');
  const more = d.getElementById('pts-more');
  const meta = d.getElementById('pts-meta');
  if (!list) return;
  const slice = store.get(SLICES.PATIENTS) || {};

  // Empty state 3 — LOAD FAILED (retry). Never a false "no patients".
  if (slice.loadError) {
    // The export reads _visibleRows: a failed reload must not leave the
    // previous render's rows behind as an exportable set (see exportPlan).
    // _visibleIds is DELIBERATELY left alone (see nextVisibleIds) — the
    // previous case set is still the one on screen underneath, so a
    // bulk-assign selection ticked against it is still valid.
    _visibleIds = nextVisibleIds(true, _visibleIds, _visibleIds);
    _visibleRows = [];
    list.innerHTML = emptyState(
      'load-error',
      'Couldn’t load patients',
      'The query failed or was denied — nothing is hidden. Tap to try again.',
      { action: 'pts-retry', label: 'Retry' }
    );
    if (more) more.innerHTML = '';
    if (meta) meta.textContent = '';
    renderAssignBar();
    renderBanner();
    return;
  }

  // FIRST LOAD STILL IN FLIGHT — keep the skeleton. Without this the registry
  // paints empty state 1 ("No patients yet") over an in-progress fetch, making
  // "still loading" indistinguishable from "genuinely empty".
  if (!casesLoaded()) {
    _visibleIds = new Set();
    _visibleRows = [];
    if (meta) meta.textContent = 'Loading patients…';
    if (more) more.innerHTML = '';
    renderBanner();
    return;
  }

  const all = allCases();

  // Empty state 1 — GENUINELY EMPTY (nothing enrolled at all).
  if (!all.length) {
    _visibleIds = new Set();
    _visibleRows = [];
    list.innerHTML = emptyState(
      'empty',
      'No patients yet',
      'Enrolments appear here automatically once consents are promoted to cases.'
    );
    if (more) more.innerHTML = '';
    if (meta) meta.textContent = registryMeta({ total: 0, inScope: 0, shown: 0, scopeText: scopeLabel(scope()), programmeName: null });
    renderAssignBar();
    renderBanner();
    return;
  }

  // Scope + search filter over the FULL set, then sort, then window.
  // An owner focus REPLACES the scope filter (see _ownerFocus above).
  const focus = _ownerFocus;
  const scoped = focus
    ? all.filter((c) => (c.assigned_to || null) === focus.uid)
    : all.filter((c) => scopeMatch(c.assigned_to || null, scope(), selfId()));
  const matched = scoped.filter((c) => matchesSearch(c, _query));
  const ordered = sortCases(matched, _sort, (id) => userName(id));
  const matchedN = matched.length;
  const windowed = ordered.slice(0, Math.min(_shown, matchedN));

  // The VISIBLE set drives selection pruning — a bulk assign must never reach
  // rows the user filtered away (see renderAssignBar).
  _visibleIds = new Set(matched.map((c) => c.case_id));
  _visibleRows = ordered;

  const prog = store.get(SLICES.PROGRAMMES) || {};
  const active = (store.get(SLICES.FILTERS) || {}).programme || 'all';
  if (meta) {
    meta.textContent = registryMeta({
      total: all.length,
      inScope: scoped.length,
      matched: matchedN,
      hasQuery: !!_query.trim(),
      shown: windowed.length,
      scopeText: _ownerFocus ? 'patients owned by ' + _ownerFocus.name : scopeLabel(scope()),
      programmeName: active !== 'all' ? pillLabel(active, prog.byCode || {}) : null,
    });
  }

  // Empty state 2 — EMPTY BECAUSE SCOPED / SEARCHED (the way out). Distinct from
  // "genuinely empty": there ARE patients, just none in this scope/search.
  if (!windowed.length) {
    const searching = !!_query.trim();
    list.innerHTML = searching
      ? emptyState('scoped', 'No matches', `No patients match “${esc(_query.trim())}”.`, { action: 'pts-clear-search', label: 'Clear search' })
      : _ownerFocus
        ? emptyState('scoped', 'No patients with this owner', `${esc(_ownerFocus.name)} owns no patients in the loaded set. Clear the filter to see the full registry.`, { action: 'pts-clear-owner', label: 'Clear owner filter' })
        : emptyState('scoped', 'No patients in this queue scope', `You’re viewing ${esc(scopeLabel(scope()))}. Widen the scope to see more.`, { action: 'pts-scope-all', label: 'Show everyone' });
    if (more) more.innerHTML = '';
    renderAssignBar();
    renderBanner();
    return;
  }

  const showSel = canBulk();
  // role="table" gives the region's aria-label effect (an aria-label on a bare
  // div is ignored) and associates each cell with its column header, so a
  // screen-reader user gets "Owner: Jane K." instead of an undifferentiated
  // stream of name/ref/county/stage/owner/risk.
  list.innerHTML =
    `<div class="pts-rhdr${showSel ? ' sel' : ''}" role="row">` +
    (showSel ? '<span role="columnheader" aria-label="Select"></span>' : '') +
    sortHead('patient_name', 'Patient') +
    sortHead('current_stage', 'Stage') +
    sortHead('owner', 'Owner') +
    sortHead('risk_level', 'Risk') +
    '<span role="columnheader" aria-label="Actions"></span>' +
    '</div>' +
    windowed.map((c) => rowHTML(c, showSel)).join('');

  // Avatar colours via CSSOM (CSP blocks inline style= parsed from innerHTML).
  list.querySelectorAll('.pts-av[data-avatar]').forEach((el) => {
    /** @type {any} */ (el).style.background = el.getAttribute('data-avatar');
  });
  list.querySelectorAll('.pts-sw[data-swatch]').forEach((el) => {
    /** @type {any} */ (el).style.background = el.getAttribute('data-swatch');
  });
  applySortClass();

  if (more) {
    const hasMore = _shown < matchedN;
    more.innerHTML = hasMore
      ? `<button type="button" class="btn" data-action="pts-more">Show more — ${windowed.length} of ${matchedN}</button>`
      : '';
  }
  renderAssignBar();
  renderBanner();
}

// A real columnheader (not role="button"), so native aria-sort applies and the
// column association reaches assistive tech. It stays keyboard-activatable via
// [data-kbd-click] — a span is not natively activatable, so the global keydown
// gate still handles it.
function sortHead(key, label) {
  return (
    `<span tabindex="0" role="columnheader" aria-sort="none" data-kbd-click data-action="pts-sort" ` +
    `data-sort="${esc(key)}" class="pts-sortable">${esc(label)}</span>`
  );
}

/** Reflect the sort state through native aria-sort on the columnheaders. */
function applySortClass() {
  const d = doc();
  const list = d && d.getElementById('pts-list');
  if (!list) return;
  list.querySelectorAll('.pts-rhdr span[data-sort]').forEach((el) => {
    el.classList.remove('asc', 'desc');
    const label = (el.textContent || '').trim();
    if (el.getAttribute('data-sort') === _sort.key) {
      el.classList.add(_sort.dir);
      const cur = _sort.dir === 'asc' ? 'ascending' : 'descending';
      const nxt = _sort.dir === 'asc' ? 'descending' : 'ascending';
      el.setAttribute('aria-sort', cur);
      el.setAttribute('aria-label', `${label}, sorted ${cur} — activate to sort ${nxt}`);
    } else {
      el.setAttribute('aria-sort', 'none');
      el.setAttribute('aria-label', `${label} — activate to sort`);
    }
  });
}

/** @param {any} c @param {boolean} showSel */
function rowHTML(c, showSel) {
  const name = c.patient_name || '—';
  const caseId = c.case_id;
  const closed = isClosedCase(c);
  const si = resolveStage(c);
  const s = STAGES[si];
  // The dot's accessible name / tooltip must be the display name, not the code.
  const dot = c.programme_code
    ? progDotHTML(programmeName(c), programmeHex(c.programme_code), 8)
    : '';

  const stageCell = closed
    ? `<div class="pts-cell" role="cell"><span class="pts-tag muted">${c.archived_at || c.is_archived ? 'Archived' : 'Closed'}</span></div>`
    : `<div class="pts-cell" role="cell"><span class="pts-stage"><span class="pts-sw" data-swatch="${esc(s.c)}" aria-hidden="true"></span>${esc(s.s)}</span></div>`;

  const owner = c.assigned_to
    ? `<span class="pts-owner">${ic(IC_USER, 12)}${esc(shortName(ownerNameOf(c)))}</span>`
    : '<span class="pts-tag unassigned">Unassigned</span>';

  const risk =
    c.risk_level === 'High' || c.risk_level === 'high'
      ? '<span class="pts-tag danger">High</span>'
      : '<span class="pts-tag muted">Standard</span>';

  // Row actions (the open ownership model). Unassigned & not closed: plain staff
  // get claim-safe Pick up + Assign; assigners get Assign. Owned & not closed:
  // Assign (reassign). Always: Open.
  let acts = '';
  if (!closed && !c.assigned_to) {
    if (!canAssign(role())) acts += `<button type="button" class="btn sm" data-action="pts-pickup" data-case="${esc(caseId)}">Pick up</button>`;
    acts += `<button type="button" class="btn sm" data-action="pts-assign" data-case="${esc(caseId)}" data-name="${esc(name)}">Assign ▾</button>`;
  } else if (!closed && c.assigned_to) {
    acts += `<button type="button" class="btn sm" data-action="pts-assign" data-case="${esc(caseId)}" data-name="${esc(name)}" data-owner="${esc(c.assigned_to)}">Assign ▾</button>`;
  }
  acts += `<button type="button" class="btn sm pri" data-action="pts-open" data-case="${esc(caseId)}">Open</button>`;

  const sel = showSel
    ? `<input type="checkbox" class="pts-sel" data-action="pts-toggle" data-case="${esc(caseId)}" ` +
      `${_sel.has(caseId) ? 'checked ' : ''}aria-label="Select ${esc(name)} for assignment"/>`
    : '';

  return (
    `<div class="pts-row${showSel ? ' sel' : ''}${closed ? ' closed' : ''}" role="row">` +
    // A LABEL, so the whole 44px cell toggles the 15px checkbox (mis-tapping a
    // dense row's checkbox feeds directly into a bulk write).
    (showSel ? `<label class="pts-selcell" role="cell">${sel}</label>` : '') +
    '<div class="pts-cell pts-pc" role="cell" data-action="pts-open" data-case="' + esc(caseId) + '" tabindex="0" data-kbd-click aria-label="' + esc(name + ' — open workspace') + '">' +
    `<span class="pts-av" aria-hidden="true" data-avatar="${esc(avatarColor(name))}">${esc(initials(name))}</span>` +
    '<span class="pts-pi">' +
    `<span class="pts-nm">${dot}<span>${esc(name)}</span></span>` +
    `<span class="pts-sub">${esc(c.reference_number || '—')} · ${esc(c.county || c.treating_facility || c.facility_name || '—')}</span>` +
    '</span></div>' +
    stageCell +
    `<div class="pts-cell" role="cell">${owner}</div>` +
    `<div class="pts-cell" role="cell">${risk}</div>` +
    `<div class="pts-cell pts-acts" role="cell">${acts}</div>` +
    '</div>'
  );
}

/**
 * The empty-state block. Three visually distinct variants share this shell but
 * carry different copy + optional CTA (the whole point is they're never
 * conflated). @returns {string}
 */
function emptyState(kind, title, sub, cta) {
  const btn = cta ? `<button type="button" class="btn sm pri" data-action="${esc(cta.action)}">${esc(cta.label)}</button>` : '';
  const icon = kind === 'load-error' ? ic(IC_WARN, 26) : ic(IC_SEARCH, 26);
  return (
    `<div class="pts-empty ${esc(kind)}">${icon}` +
    `<h2>${esc(title)}</h2><p>${esc(sub)}</p>${btn}</div>`
  );
}

// ─── Bulk assign bar (canAssign; legacy updateAssignBar / execBulkAssign) ───

function renderAssignBar() {
  const d = doc();
  const bar = d && d.getElementById('pts-assign-bar');
  if (!bar) return;
  // Prune the selection to what is CURRENTLY VISIBLE (scope + search), not to
  // the full loaded set. Pruning against allCases() let a user tick 15 rows,
  // then type a search, and press Assign — writing to the 12 patients now off
  // screen, with nothing on screen corresponding to the count. The selection
  // still survives a realtime re-render (it is module-held); it is only
  // narrowed when the user themselves narrows the view.
  const before = _sel.size;
  for (const id of [..._sel]) if (!_visibleIds.has(id)) _sel.delete(id);
  const dropped = before - _sel.size;

  if (!canBulk() || _sel.size === 0) {
    bar.hidden = true;
    bar.innerHTML = '';
    return;
  }
  const opts =
    '<option value="">Assign to…</option>' +
    assignableUsers(usersMap())
      .map((u) => `<option value="${esc(u.user_id)}"${u.user_id === _assignPick ? ' selected' : ''}>${esc(u.full_name || '—')} (${esc(u.role || 'nurse')})</option>`)
      .join('');
  bar.hidden = false;
  // Say so when the view narrowing dropped rows — a silent shrink of a bulk
  // selection is exactly the kind of thing a user must not discover afterwards.
  const droppedNote = dropped
    ? `<span class="pts-ab-note">${esc(String(dropped))} deselected — no longer in view</span>`
    : '';
  bar.innerHTML =
    `<b class="pts-ab-count">${esc(String(_sel.size))} selected</b>` +
    droppedNote +
    `<label class="pts-ab-lbl" for="pts-assign-user">Assign to…</label>` +
    `<select id="pts-assign-user" class="pts-ab-sel" aria-label="Assign selected patients to">${opts}</select>` +
    '<button type="button" class="btn sm pri" data-action="pts-bulk-assign">Assign</button>' +
    '<button type="button" class="btn sm ghost" data-action="pts-bulk-clear">Clear</button>';
}

function onToggleRow(el) {
  const id = el.getAttribute('data-case');
  if (!id) return;
  if (/** @type {any} */ (el).checked) _sel.add(id);
  else _sel.delete(id);
  renderAssignBar();
}

async function onBulkAssign() {
  // canBulk(), not canAssign() alone — the handler's gate must be no narrower
  // than the one that decided to SHOW the bar (simple mode hides bulk assign).
  if (!canBulk()) {
    toast('You don’t have assignment rights', 'err');
    return;
  }
  const d = doc();
  const sel = d && d.getElementById('pts-assign-user');
  const uid = sel ? sel.value : '';
  _assignPick = uid;
  const ids = [..._sel];
  if (!ids.length) {
    toast('Select at least one patient', 'err');
    return;
  }
  if (!uid) {
    toast('Choose who to assign to', 'err');
    return;
  }
  const btn = d && d.querySelector('[data-action="pts-bulk-assign"]');
  if (btn) {
    /** @type {any} */ (btn).disabled = true;
    btn.textContent = 'Assigning…';
  }
  const res = await bulkAssign(ids, uid, { cases: allCases() }, undefined);
  const data = res.data || {};
  if (!res.ok) {
    const failed = data.failed || ids.length;
    toast(netErr(res.error, `${failed} of ${ids.length} assignments failed — the rest saved`), 'err');
  } else {
    const done = data.assigned || 0;
    const skip = data.skipped ? ` — ${data.skipped} already with ${userName(uid)}` : '';
    toast(`${done} patient${done === 1 ? '' : 's'} assigned to ${userName(uid)}${skip}`, 'ok');
  }
  // Clear the selection whether or not every row landed (the ones that saved are
  // reflected on reload; a partial failure was already surfaced).
  _sel.clear();
  _assignPick = '';
  // READ-AFTER-WRITE: force a fresh fetch. The shared single-flight could
  // otherwise satisfy this await with a snapshot taken before the assignment
  // landed, rendering stale owners with no follow-up render.
  await refresh({ force: true });
}

function onBulkClear() {
  _sel.clear();
  renderRegistry();
}

// ─── Programme banner ──────────────────────────────────────────────────────

function renderBanner() {
  const d = doc();
  const el = d && d.getElementById('pts-banner');
  if (!el) return;
  const active = (store.get(SLICES.FILTERS) || {}).programme || 'all';
  // The owner focus takes the banner: it overrides the queue scope, so it must
  // announce itself and carry its own way out.
  const ownerHTML = _ownerFocus
    ? '<span>Showing only patients owned by <strong>' + esc(_ownerFocus.name) + '</strong> — the queue scope is bypassed while this filter is on. ' +
      'Reassign them before deactivating the account; nothing here has been un-owned. ' +
      'This list includes closed and archived cases, so it can be longer than the open-caseload count in the offboarding warning.</span>' +
      '<button type="button" class="btn sm ghost" data-action="pts-clear-owner">Clear owner filter</button>'
    : '';
  if (active === 'all' && !ownerHTML) {
    el.hidden = true;
    el.innerHTML = '';
    return;
  }
  const prog = store.get(SLICES.PROGRAMMES) || {};
  el.hidden = false;
  el.innerHTML = ownerHTML + (active === 'all' ? '' : programmeBannerCopy(pillLabel(active, prog.byCode || {}), 'patients'));
}

/**
 * Focus the registry on ONE owner's caseload and navigate to it (R-078: the
 * Admin console's "reassign them first" link). Exported so the Admin view never
 * reaches into this module's state directly.
 * @param {string} uid app_users.user_id
 * @param {string} name display name for the banner
 * @returns {void}
 */
export function openRegistryForOwner(uid, name) {
  if (!uid) return;
  _pendingOwnerFocus = { uid, name: name || 'this user' };
  _query = '';
  _shown = PT_PAGE;
  // navigate() assigns location.hash and the router answers on hashchange —
  // ASYNCHRONOUSLY. So we must NOT consume the pending focus here: render()
  // does it when the route actually arrives. Consuming synchronously cleared
  // the pending value before render() ever saw it, and the hand-off banner
  // silently stopped appearing at all.
  const alreadyHere = ((store.get(SLICES.UI) || {}).view === 'patients');
  navigate('patients');
  if (alreadyHere) {
    // Same route => no hashchange => render() will not run. Consume it here.
    _ownerFocus = _pendingOwnerFocus;
    _pendingOwnerFocus = null;
    renderRegistry();
  }
}

// ─── CSV export (admin-only) ────────────────────────────────────────────────

function onExport() {
  if (!isAdmin(role())) {
    toast('CSV export is admin-only', 'err');
    return;
  }
  // Export WHAT THE ADMIN IS LOOKING AT — the scoped + searched set, not the
  // full programme. These columns are a PII extract (name, phone, county,
  // facility); an export that silently exceeds the visible filter hands out
  // records the admin deliberately filtered away. The render window is NOT
  // applied (that's a display slice, not a filter), so "Show more" state can't
  // truncate the file. exportPlan() never falls back to the full set: an empty
  // view exports nothing, and says so.
  const slice = store.get(SLICES.PATIENTS) || {};
  const plan = exportPlan({ visibleRows: _visibleRows, loaded: casesLoaded(), loadError: slice.loadError });
  if (plan.reason !== 'ok') {
    toast(
      plan.reason === 'load-error'
        ? 'Patients did not load — nothing to export'
        : plan.reason === 'not-loaded'
          ? 'Patients are still loading — try again in a moment'
          : 'No patients in the current scope and search — nothing to export',
      'err'
    );
    return;
  }
  const rows = plan.rows;
  const narrowed = rows.length !== allCases().length;
  const csv = buildPatientsCsv(rows, {
    programmeName: (c) => programmeName(c),
    ownerName: (id) => userName(id),
  });
  const d = doc();
  const blob = new /** @type {any} */ (globalThis).Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8' });
  const url = /** @type {any} */ (globalThis).URL.createObjectURL(blob);
  const a = d.createElement('a');
  a.href = url;
  a.download = `tumaini-patients-${new Date().toISOString().slice(0, 10)}.csv`;
  d.body.appendChild(a);
  a.click();
  d.body.removeChild(a);
  /** @type {any} */ (globalThis).URL.revokeObjectURL(url);
  toast(
    narrowed
      ? `Exported ${rows.length} of ${allCases().length} patients (current scope and search)`
      : `Exported ${rows.length} patients as CSV`,
    'ok'
  );
}

// ─── Search ─────────────────────────────────────────────────────────────────

function onSearchInput(el) {
  _query = el.value || '';
  _shown = PT_PAGE; // a fresh search starts at the top of the window
  renderRegistry();
}

// ─── Actions / refresh ───────────────────────────────────────────────────────

/**
 * Pure: the Patients sidebar badge after a reload (review R-13). A committed
 * load counts the set; a FAILED reload asserts no count at all (null — the
 * shell hides the badge). loadAllCases keeps the previous rows on failure so
 * the board does not blank, but the badge reading those rows would state a
 * number under a registry that says "Couldn't load patients" — after a
 * programme switch, the previous programme's number.
 * @param {any} res the loadAllCases / forceReloadAllCases envelope
 * @param {number} count allCases().length
 * @returns {number|null}
 */
export function patientsBadgeCount(res, count) {
  return res && res.ok ? count : null;
}

/**
 * Reload the case set and re-render. `opts.force` bypasses the shared
 * single-flight for read-after-write callers (post-bulk-assign) — see
 * forceReloadAllCases. Also loads the staff directory when it is empty, so
 * landing straight on Patients (deep link, or the Today legacy-pool drain)
 * doesn't render every owner as "A team member" and an unusable assign picker.
 * @param {{force?: boolean}} [opts]
 */
async function refresh(opts) {
  const dir = Object.keys(usersMap()).length ? null : loadUsers();
  const r = await (opts && opts.force ? forceReloadAllCases() : loadAllCases());
  if (dir) await dir; // never throws; a miss only degrades names
  if (r && r.stale) return;
  // Keep the sidebar badge honest (legacy cnt-p) — no number after a failed
  // reload (R-13).
  const counts = store.get(SLICES.COUNTS) || {};
  store.set(SLICES.COUNTS, Object.assign({}, counts, { patients: patientsBadgeCount(r, allCases().length) }));
  renderRegistry();
}

function wireOnce() {
  if (_wired) return;
  _wired = true;

  register('pts-retry', () => void refresh());
  register('pts-more', () => {
    _shown += PT_PAGE;
    renderRegistry();
  });
  register('pts-sort', (el) => {
    const key = el.getAttribute('data-sort');
    if (key) {
      _sort = nextSort(_sort, key);
      renderRegistry();
    }
  });
  register('pts-clear-search', () => {
    _query = '';
    const d = doc();
    const inp = d && d.getElementById('pts-search');
    if (inp) /** @type {any} */ (inp).value = '';
    renderRegistry();
  });
  register('pts-clear-owner', () => {
    _ownerFocus = null;
    _shown = PT_PAGE;
    renderRegistry();
  });
  register('pts-scope-all', () => {
    // The way out of the scoped-empty state — widen to everyone (shared scope).
    setScopeMode(selfId(), 'all', role());
    void refresh();
  });
  register('pts-open', (el) => {
    const id = el.getAttribute('data-case');
    if (id) void openWorkspace(id);
  });
  register('pts-pickup', (el) => {
    const id = el.getAttribute('data-case');
    if (id) void pickUp(id, { onDone: () => void refresh() });
  });
  register('pts-assign', (el) => {
    const id = el.getAttribute('data-case');
    const name = el.getAttribute('data-name') || '';
    const owner = el.getAttribute('data-owner') || null;
    if (id) openAssignModal(id, name, owner, { onDone: () => void refresh() });
  });
  register('pts-toggle', (el) => onToggleRow(el));
  register('pts-bulk-assign', () => void onBulkAssign());
  register('pts-bulk-clear', () => onBulkClear());
  register('pts-export', () => onExport());

  const d = doc();
  if (d) {
    d.addEventListener('input', (/** @type {any} */ e) => {
      if (e.target && e.target.id === 'pts-search') onSearchInput(e.target);
    });
  }

  // Re-render on the shared cases slice (loadAllCases writes it) — selection is
  // module-held, so it survives.
  store.subscribe(SLICES.PATIENTS, () => renderRegistry());
  store.subscribe(SLICES.FILTERS, () => {
    if (mounted()) void refresh();
  });
  // Realtime: cases changes fan out to 'patients'/'pipeline'. Reload through the
  // SINGLE cases writer; the ticked-boxes Set is untouched by the re-render.
  store.subscribe(SLICES.RT_CHANGES, (v) => {
    if (mounted() && v && Array.isArray(v.changed) && (v.changed.includes('patients') || v.changed.includes('pipeline'))) {
      void refresh();
    }
  });
}

// ─── View registration ──────────────────────────────────────────────────────

function skeleton() {
  const admin = isAdmin(role());
  return (
    '<div id="patients-view" class="patients">' +
    '<header class="pts-head">' +
    '<div><h1 class="pts-h">Patients</h1><p id="pts-meta" class="pts-meta"></p></div>' +
    '<div class="pts-actions">' +
    '<span class="pts-search-wrap">' +
    ic(IC_SEARCH, 15) +
    '<input id="pts-search" class="pts-search" type="search" placeholder="Search name, ref, phone, facility…" aria-label="Search patients"/>' +
    '</span>' +
    (admin ? `<button type="button" class="btn" data-action="pts-export">${ic(IC_DOWN, 15)}Export CSV</button>` : '') +
    `<button type="button" class="btn" data-action="pts-board">${ic(IC_USER, 15)}Board</button>` +
    '</div>' +
    '</header>' +
    '<div id="pts-banner" class="pts-banner" hidden></div>' +
    '<div id="pts-assign-bar" class="pts-assign-bar" role="region" aria-label="Bulk assign selected patients" hidden></div>' +
    '<div id="pts-list" class="pts-list" role="table" aria-label="Patients"><div class="skel"></div><div class="skel"></div><div class="skel"></div></div>' +
    '<div id="pts-more" class="pts-more"></div>' +
    '</div>'
  );
}

/**
 * Register the Patients registry view, replacing the Phase-1 placeholder. Call
 * once at boot from main.js (and the dev harness).
 * @returns {void}
 */
export function registerPatients() {
  register('pts-board', () => navigate('board'));
  registerView('patients', {
    render(host) {
      // Consume the one-shot owner focus: set when we got here via the
      // offboarding hand-off, null for every other route in.
      _ownerFocus = _pendingOwnerFocus;
      _pendingOwnerFocus = null;
      host.innerHTML = skeleton();
      wireOnce();
      renderRegistry(); // first paint from whatever is already in the store
    },
    load() {
      return refresh();
    },
  });
}
